import { useState, useEffect, useRef } from "react"
import { User } from "../utils/interfaces"
import ErrorIcon from "shared/components/icons/error_icon"
import classnames from "classnames"
import parse from "html-react-parser"

import styles from "./intro.module.scss"
import arrowRightIcon from "images/icons/icon-arrow-right.svg?raw"
import { isPaidTier } from "shared/utils/tiers"
import { fitPromptHeight, wirePromptField } from "app/utils/prompt_field"
import { formatPromptCount, isPromptAtLimit, showPromptCount } from "app/utils/prompt_length"

// Mirrors Web::PromptHero (app/models/web/prompt_hero.rb), the single source
// for this copy — it arrives through the react_component `introCopy` prop.
export interface IntroCopy {
  title: string
  placeholder: string
  ctaLabel: string
  examplesLabel: string
  examples: string[]
}

interface Props {
  active: boolean
  user: User
  onSubmit: (prompt: string) => void
  copy: IntroCopy
  initialPrompt?: string
}

const Intro = ({ active, user, onSubmit, copy, initialPrompt }: Props) => {
  const [prompt, setprompt] = useState(initialPrompt ?? "")
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const fieldRef = useRef<HTMLDivElement>(null)
  const [errorMessage, updateErrorMessage] = useState("")
  const atLimit = isPromptAtLimit(prompt.length)

  useEffect(() => {
    textAreaRef.current?.focus()
  }, [active])

  useEffect(() => {
    const textArea = textAreaRef.current
    if (textArea) return wirePromptField(fieldRef.current, textArea)
  }, [])

  useEffect(() => {
    if (textAreaRef.current) fitPromptHeight(textAreaRef.current)
  }, [prompt])

  // Puts the cursor after a prefilled value, once. Later `active` toggles refocus
  // above but must not move a cursor the user has already positioned.
  useEffect(() => {
    const textArea = textAreaRef.current
    textArea?.setSelectionRange(textArea.value.length, textArea.value.length)
  }, [])

  const onContinue = () => {
    const message = (textAreaRef.current?.value ?? "").trim()

    if (!isPaidTier(user.tier)) {
      window.App.Utils?.logCustomDatadogAction?.("free_user_ai_prompt", { message })
    }

    updateErrorMessage("")

    if (message.length === 0) {
      updateErrorMessage("Please enter a prompt.")
      return
    }

    onSubmit(message)
  }

  return (
    <section className={styles["chat-section"]}>
      <h1 className={styles["heading"]}>{copy.title}</h1>
      <div className={styles["input-area"]}>
        <div
          ref={fieldRef}
          className={classnames(styles["chat-field"], {
            [styles["with-errors"]]: !!errorMessage,
            [styles["at-limit"]]: atLimit,
          })}
        >
          <textarea
            ref={textAreaRef}
            placeholder={copy.placeholder}
            rows={1}
            maxLength={500}
            value={prompt}
            onFocus={() => {
              if (errorMessage) updateErrorMessage("")
            }}
            onKeyDown={evt => {
              if (evt.key === "Enter" && !evt.shiftKey) {
                evt.preventDefault()
                onContinue()
              }
            }}
            onChange={evt => {
              setprompt(evt.target.value)
              if (errorMessage) updateErrorMessage("")
            }}
          />
          <div className={styles["actions"]}>
            {showPromptCount(prompt.length) && (
              <span className={classnames(styles["count"], { [styles["at-limit"]]: atLimit })} aria-live="polite">
                {formatPromptCount(prompt.length)}
              </span>
            )}
            <ContinueButton onContinue={onContinue} empty={prompt.length === 0} label={copy.ctaLabel} />
          </div>
        </div>
        {errorMessage && (
          <div className={styles["hint-helper"]}>
            <span className={styles["error-message"]}>
              <ErrorIcon width="24" height="24" />
              <span className={styles["badge"]}>{errorMessage}</span>
            </span>
          </div>
        )}
      </div>
      <div className={styles["examples"]}>
        <p>{copy.examplesLabel}</p>
        <ul>
          {copy.examples.map((example, index) => (
            <li key={index}>
              <button
                onClick={() => {
                  setprompt(example)
                  updateErrorMessage("")
                }}
              >
                {example}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}

const ContinueButton = ({ onContinue, empty, label }: { onContinue: () => void; empty: boolean; label: string }) => (
  <button id="continue" className={styles["start-building"]} onClick={onContinue} disabled={empty}>
    <span>{label}</span>
    <span className={styles["arrow-icon"]}>{parse(arrowRightIcon)}</span>
  </button>
)

export default Intro
